import React, { useEffect, useRef, useState } from 'react';
import { naytiveAPI } from '../services/api';
import Sidebar from '../components/Sidebar';
import './VoicePicker.css';

/**
 * Lets ops preview every Gemini Live voice the voice-agent can use
 * for Kim. Each card streams a pre-generated WAV sample of Kim's
 * greeting line. Selecting a voice doesn't auto-apply (that needs an
 * env flip on the voice-agent and a restart) — the page just tells
 * you which one to set as GEMINI_VOICE.
 */
const VoicePicker = () => {
  const [voices, setVoices] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [playing, setPlaying] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    naytiveAPI.listVoices()
      .then((res) => {
        if (cancelled) return;
        setVoices(res.data.voices || []);
        setActive(res.data.active || null);
      })
      .catch((err) => !cancelled && setError(err?.response?.data?.message || err.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  const handlePlay = (voice) => {
    // Stop any current playback before starting a new one
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (playing === voice) {
      setPlaying(null);
      return;
    }
    const audio = new Audio();
    audio.crossOrigin = 'use-credentials';
    audio.src = naytiveAPI.voiceSampleUrl(voice);
    audio.onended = () => setPlaying(null);
    audio.onerror = () => { setPlaying(null); setError(`Couldn't play ${voice}`); };
    audio.play().then(() => setPlaying(voice)).catch(() => setPlaying(null));
    audioRef.current = audio;
  };

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="dashboard-main">
        <div className="vp-page">
          <h1 className="vp-title">Voice Picker</h1>
          <p className="vp-subtitle">
            Preview every Gemini Live voice available for Kim. Each clip is the
            opening line of a verification call ("Hi, this is Kim from Naytive…").
            Pick one, then set <code>GEMINI_VOICE=&lt;name&gt;</code> in
            <code> user-panel-v3/voice-agent/.env.local</code> and restart the
            voice-agent service.
          </p>

          {error && <div className="vp-error">{error}</div>}

          {loading ? (
            <div className="vp-empty">Loading voices…</div>
          ) : voices.length === 0 ? (
            <div className="vp-empty">
              No samples found. Generate them once with:<br />
              <code>cd user-panel-v3/voice-agent && source .venv/bin/activate && python scripts/generate_voice_samples.py</code>
            </div>
          ) : (
            <div className="vp-grid">
              {voices.map((v) => {
                const isActive = v.name === active;
                const isPlaying = playing === v.name;
                return (
                  <div key={v.name} className={`vp-card${isActive ? ' vp-card-active' : ''}`}>
                    <div className="vp-card-head">
                      <span className="vp-name">{v.name}</span>
                      <span className={`vp-gender vp-gender-${v.gender}`}>{v.gender}</span>
                    </div>
                    <div className="vp-blurb">{v.blurb}</div>
                    <button
                      className={`vp-play-btn${isPlaying ? ' vp-play-btn-playing' : ''}`}
                      onClick={() => handlePlay(v.name)}
                    >
                      {isPlaying ? (
                        <>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="5" width="4" height="14" /><rect x="14" y="5" width="4" height="14" /></svg>
                          Stop
                        </>
                      ) : (
                        <>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3" /></svg>
                          Play sample
                        </>
                      )}
                    </button>
                    {isActive && <div className="vp-active-pill">Active</div>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default VoicePicker;
